import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { TProfileCalendarApiResponse } from '../../../../apis/profile/profile.interfaces';
import { TEventsPrimary } from '../../../../apis/events/events.interfaces';
import { useProfileCalendarListHook } from '../hooks/useProfileCalendarListHook';
import { useProfileCalendarItemHook } from '../hooks/useProfileCalendarItemHook';

type TProfileCalendarState = {
  calendarEvents: TEventsPrimary[];
  selectedEventId: string | null;
  selectedEvent: TEventsPrimary | null;
  isLoadingList: boolean;
  isLoadingItem: boolean;
  errorMessage?: string;
  totalCount: number;
};

enum ACTION_TYPE {
  SET_CALENDAR_EVENTS = 'SET_CALENDAR_EVENTS',
  SET_SELECTED_EVENT_ID = 'SET_SELECTED_EVENT_ID',
  SET_SELECTED_EVENT = 'SET_SELECTED_EVENT',
  FETCHING_LIST = 'FETCHING_LIST',
  FETCHING_ITEM = 'FETCHING_ITEM',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
}

type Action =
  | {
      type: ACTION_TYPE.SET_CALENDAR_EVENTS;
      payload: TProfileCalendarApiResponse;
    }
  | { type: ACTION_TYPE.SET_SELECTED_EVENT_ID; payload: string | null }
  | { type: ACTION_TYPE.SET_SELECTED_EVENT; payload: TEventsPrimary | null }
  | { type: ACTION_TYPE.FETCHING_LIST; payload: boolean }
  | { type: ACTION_TYPE.FETCHING_ITEM; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string };

const initialState: TProfileCalendarState = {
  calendarEvents: [],
  selectedEventId: null,
  selectedEvent: null,
  isLoadingList: false,
  isLoadingItem: false,
  errorMessage: undefined,
  totalCount: 0,
};

const ProfileCalendarContext = createContext<TProfileCalendarState | undefined>(
  undefined
);
const ProfileCalendarDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

const profileCalendarReducer = (
  state: TProfileCalendarState,
  action: Action
): TProfileCalendarState => {
  switch (action.type) {
    case ACTION_TYPE.SET_CALENDAR_EVENTS:
      return {
        ...state,
        calendarEvents: action.payload.data,
        totalCount: action.payload.total,
        errorMessage: undefined,
      };
    case ACTION_TYPE.SET_SELECTED_EVENT_ID:
      return {
        ...state,
        selectedEventId: action.payload,
        selectedEvent: action.payload ? state.selectedEvent : null,
      };
    case ACTION_TYPE.SET_SELECTED_EVENT:
      return {
        ...state,
        selectedEvent: action.payload,
      };
    case ACTION_TYPE.FETCHING_LIST:
      return {
        ...state,
        isLoadingList: action.payload,
        errorMessage: action.payload ? undefined : state.errorMessage,
      };
    case ACTION_TYPE.FETCHING_ITEM:
      return {
        ...state,
        isLoadingItem: action.payload,
      };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return {
        ...state,
        isLoadingList: false,
        isLoadingItem: false,
        errorMessage: action.payload,
      };
    default:
      throw new Error('Unhandled action type');
  }
};

export const ProfileCalendarProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(profileCalendarReducer, initialState);

  const {
    data: calendarListResponse,
    isLoading: isLoadingList,
    error: listError,
  } = useProfileCalendarListHook();

  const {
    data: calendarItem,
    isLoading: isLoadingItem,
    error: itemError,
  } = useProfileCalendarItemHook(state.selectedEventId || '', {
    enabled: !!state.selectedEventId,
  });

  useEffect(() => {
    dispatch({ type: ACTION_TYPE.FETCHING_LIST, payload: isLoadingList });
  }, [isLoadingList]);

  useEffect(() => {
    if (calendarListResponse) {
      dispatch({
        type: ACTION_TYPE.SET_CALENDAR_EVENTS,
        payload: calendarListResponse,
      });
    }
  }, [calendarListResponse]);

  useEffect(() => {
    if (listError) {
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload:
          listError?.response?.data?.message ||
          'Failed to fetch calendar events',
      });
    }
  }, [listError]);

  useEffect(() => {
    dispatch({ type: ACTION_TYPE.FETCHING_ITEM, payload: isLoadingItem });
  }, [isLoadingItem]);

  useEffect(() => {
    if (calendarItem) {
      dispatch({
        type: ACTION_TYPE.SET_SELECTED_EVENT,
        payload: calendarItem,
      });
    }
  }, [calendarItem]);

  useEffect(() => {
    if (itemError) {
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload:
          itemError?.response?.data?.message ||
          'Failed to fetch calendar event details',
      });
    }
  }, [itemError]);

  const value = useMemo(
    () => ({
      ...state,
      isLoadingList,
      isLoadingItem,
    }),
    [state, isLoadingList, isLoadingItem]
  );

  return (
    <ProfileCalendarContext.Provider value={value}>
      <ProfileCalendarDispatchContext.Provider value={dispatch}>
        {children}
      </ProfileCalendarDispatchContext.Provider>
    </ProfileCalendarContext.Provider>
  );
};

export const useProfileCalendarState = () => {
  const context = useContext(ProfileCalendarContext);
  if (context === undefined) {
    throw new Error(
      'useProfileCalendarState must be used within a ProfileCalendarProvider'
    );
  }
  return context;
};

export const useProfileCalendarActions = () => {
  const dispatch = useContext(ProfileCalendarDispatchContext);
  if (dispatch === undefined) {
    throw new Error(
      'useProfileCalendarActions must be used within a ProfileCalendarProvider'
    );
  }

  const setSelectedEventId = (id: string | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_EVENT_ID, payload: id });
  };

  const setSelectedEvent = (event: TEventsPrimary | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_EVENT, payload: event });
  };

  const clearSelectedEvent = () => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_EVENT_ID, payload: null });
    dispatch({ type: ACTION_TYPE.SET_SELECTED_EVENT, payload: null });
  };

  return {
    setSelectedEventId,
    setSelectedEvent,
    clearSelectedEvent,
  };
};
